import { processNaaBridgeMessage } from './processNaaBridgeMessage';
import type { NestedAppAuthExecutor } from 'owa-nested-app-auth-types';
import { isBridgeRequestEnvelope } from 'owa-nested-app-auth-types/lib/msal-types/BridgeRequestEnvelope';
import { logStartUsage } from 'owa-analytics-start';
import { getApplicationSettings } from 'owa-application-settings';
import { version as msalVersion } from '@azure/msal-browser-1p';
import type { MailboxInfo } from 'owa-client-types';
import { isBootFeatureEnabled } from 'owa-metatags/lib/isBootFeatureEnabled';

/**
 * Determines if request is a Nested App Auth request, and if it is it will process
 * the request using the specified executor
 * @param request NAA request payload; `BridgeRequestEnvelope` serialized as JSON
 * @param executor Executor for Nested App Auth
 * @param mailboxInfo The MailboxInfo for the account for which the request is to be executed
 * @param origin Origin URL of the app making the request
 * @returns Promise that resolves a string; `BridgeResponseEnvelope` serialized as JSON
 */
export async function executeNaaRequest(
    request: string,
    executor: NestedAppAuthExecutor,
    mailboxInfo: MailboxInfo,
    origin: string,
    isTrustedOrigin: boolean,
    targetWindow: Window,
    scenarioName: string,
    callerId: string
): Promise<string> {
    const { host, protocol } = new URL(origin);

    if (protocol !== 'https:' || !host) {
        throw new Error('Invalid origin');
    }

    // parse the request. If this fails, it'll throw and reject this promise, which is all we can do
    // without a valid requestId to construct a NAA error.
    const bridgeRequest = JSON.parse(request);

    if (
        typeof bridgeRequest !== 'object' ||
        bridgeRequest === null ||
        !isBridgeRequestEnvelope(bridgeRequest)
    ) {
        throw new Error('Invalid request payload');
    }

    // For configured NAA scenarios, instruct MSAL to exclude OWA's configured CP1
    // clientCapabilities from the xms_cc claims sent to Entra. Some nested apps do
    // not handle CAE, so they should not receive a CAE-capable token issued under
    // the host's capability. The scenario list is ECS-overridable, and the feature
    // remains gated by a boot flight for staged rollout.
    const skipBrokerClaims =
        isBootFeatureEnabled('auth-msaljs-naa-skipBrokerClaims-addin') &&
        getApplicationSettings('NestedAppAuth').skipBrokerClaimsScenarios.includes(scenarioName);

    const bridgeResponse = await processNaaBridgeMessage(
        bridgeRequest,
        executor,
        host,
        isTrustedOrigin,
        targetWindow,
        skipBrokerClaims
    );

    logStartUsage('NAARequest', mailboxInfo, {
        scenarioName,
        callerId,
        method: bridgeRequest.method,
        success: bridgeResponse.success,
        duration: bridgeRequest.sendTime ? Date.now() - bridgeRequest.sendTime : undefined,
        expiresIn: bridgeResponse.token?.expires_in,
        status: bridgeResponse.error?.status,
        code: bridgeResponse.error?.code,
        subError: bridgeResponse.error?.subError,
        errorDescription: bridgeResponse.error?.description,
        correlationId: bridgeRequest.tokenParams?.correlationId,
        isTrustedOrigin,
        msalVersion,
        skipBrokerClaims,
    });

    return JSON.stringify(bridgeResponse);
}
